<template>
  <div class="w-full m-auto p-4 sm:py-6 overflow-hidden">
    <header class="relative z-10 mb-6 mx-auto max-w-150px sm:max-w-200px">
      <router-link v-if="!logoNoLinkRoutes.includes($route.name)" to="/" :title="$t('regWizPopUp.toHome')">
        <UnraidLogo gradientStart="#ffffff" gradientStop="#ffffff" />
      </router-link>
      <UnraidLogo v-else gradientStart="#ffffff" gradientStop="#ffffff" />
      <UiServerInfo v-if="!loading && state" />
    </header>
    <transition name="fade" mode="out-in">
      <div v-if="loading" class="flex flex-col text-center">
        <UnraidLoading :height="64" gradientStart="#ffffff" gradientStop="#ffffff" />
      </div>
      <router-view v-else class="relative w-full" />
    </transition>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import { UnraidLogo, UnraidLoading } from '@unraid/shared-components';
import semverGte from 'semver/functions/gte';
import UiServerInfo from './components/Ui/ServerInfo.vue';
/**
 * Why not in main.js?
 * @see https://cli.vuejs.org/guide/build-targets.html#using-vuex-in-builds
 */
import i18n from './i18n';
import router from './router';
import store from './store';
import { log } from './log';

import { CAN_POST_MESSAGE, WEBGUI_PARENT } from './consts';
import { NODE_ENV } from './env';
/**
 * This allows us to load the default translations for Reg Wiz.
 * Doing this makes the UPC web components smaller when built.
 */
function loadLocaleMessages() {
  const locales = require.context(
    './locales',
    true,
    /[A-Za-z0-9-_,\s]+\.json$/i,
  );
  const messages = {};
  locales.keys().forEach((key) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      messages[locale] = locales(key);
    }
  });
  return messages;
}

export default {
  i18n,
  router,
  store,
  name: 'App',
  metaInfo: {
    title: 'Registration Wizard',
    titleTemplate: NODE_ENV === 'production'
      ? '%s | Unraid'
      : '[🚧🚨] %s | Unraid',
  },
  components: {
    UnraidLogo,
    UnraidLoading,
    UiServerInfo,
  },
  data: () => ({
    checkForSession: null,
    logoNoLinkRoutes: [
      'VerifyEmail',
      'VerifyEmailResend',
      'ExtendTrial',
      'Success',
      'NotAllowed',
      'NotFound',
    ],
  }),
  computed: {
    ...mapState([
      'forumaccount',
      'guid',
      'guidRegistered',
      'guidValid',
      'loading',
      'registered',
      'replaceable',
      'state',
      'postMessageError',
      'apiVersion',
      'hideMyServers',
    ]),
    ...mapGetters(['stateData']),
  },
  methods: {
    ...mapActions([
      'receivePostMessage',
      'validateGuid',
    ]),
    ...mapMutations([
      'SET_LOADING_STATE',
      'SET_APP_ERROR_MESSAGE',
      'SET_APP_ERROR_MESSAGE_SUBTEXT',
    ]),
  },
  watch: {
    /**
     * Watches change of guidValid `false` to `true`.
     * If we're Signed Out with state of TRIAL,BASIC,PLUS, or PRO
     * Then we need to determine where to redirect the user.
     * Which is based off of `forumaccount` which comes from the
     * /validate/guid response from key server
     */
    // eslint-disable-next-line consistent-return
    guidValid(newVal, oldVal) {
      if (this.$router.currentRoute.name === 'ExtendTrial') return false; // don't need to sign in to extend
      // send to home when guid not valid
      if (this.state === 'EGUID' && newVal === false) {
        log.debug('[watcher guidValid] 02', this.state, newVal);
        return (this.$router.currentRoute.name !== 'Registration') ? this.$router.push({ name: 'Registration' }) : false;
      }
      if (newVal && this.state === 'EGUID') { // valid and EGUID
        log.debug('[watcher guidValid] 03', this.state, newVal, this.replaceable);
        if (this.replaceable) { // user is allowed to replace, so redirect them there
          return (this.$router.currentRoute.name !== 'ReplaceKey')
            ? this.$router.push({ name: 'ReplaceKey' })
            : log.debug('[SKIP $router.push]');
        }
        return (this.$router.currentRoute.name !== 'Registration') // user not allowed to replace, redirect them away if necessary
          ? this.$router.push({ name: 'Registration' })
          : log.debug('[SKIP $router.push]');
      }
      if (!this.hideMyServers && this.apiVersion && this.apiVersion !== '0.0.0') { // need api for sign in redirects
        log.debug('[watcher guidValid] 01', newVal, oldVal);
        // guid is valid determine where to put user
        if (newVal && !oldVal) {
          // handle redirect to Sign In / Sign Up based on server state data
          if ((this.state === 'ENOKEYFILE' || this.stateData.withKey || this.stateData.error?.signInToFix) && !this.registered) {
            log.debug('[watcher guidValid] 04', this.state, this.stateData.withKey, this.stateData.error?.signInToFix, this.registered);
            if (this.forumaccount || this.guidRegistered) {
              log.debug('[watcher guidValid] 05', this.forumaccount, this.guidRegistered);
              return (this.$router.currentRoute.name !== 'Deprecated')
                ? this.$router.push({ name: 'Deprecated' })
                : log.debug('[SKIP $router.push]');
            }
            if (this.forumaccount === false) {
              log.debug('[watcher guidValid] 06', this.forumaccount);
              // we want the user to pick sign in / up. because a TRIAL user may or may not have a forum account already
              if (this.state === 'TRIAL') {
                log.debug('[watcher guidValid] 07', this.state);
                return (this.$router.currentRoute.name !== 'Registration') ? this.$router.push({ name: 'Registration' }) : false;
              }
              log.debug('[watcher guidValid] 08');
              return (this.$router.currentRoute.name !== 'SignUp')
                ? this.$router.push({ name: 'SignUp' })
                : log.debug('[SKIP $router.push]');
            }
          }
        }
      }
    },
    state(newVal, oldVal) { // if user changes to state that can't be fixed by sign in redirect them to home page of pop-up to display error info
      if (this.stateData.error && !this.stateData.error.signInToFix) {
        log.debug('[state] changed to error state %o', { oldVal, newVal });
        const disallowedRoutesToRedirectFrom = ['Registration', 'Success', 'SignOut', 'ExtendTrial']; // do not redirect away from these pages
        // slight delay used to prevent double nagivation attempt to route since this.validateGuid().then()… in beforeMount() might trigger the same route navigation.
        return setTimeout(() => ((!disallowedRoutesToRedirectFrom.includes(this.$router.currentRoute.name))
          ? this.$router.push({ name: 'Registration' })
          : log.debug('[SKIP $router.push]')), 100);
      }
      return log.debug('[state] changed %o', { oldVal, newVal });
    },
    /**
     * Common errors showing in support
     * - Reverse Proxy – 'data.server.site' === http://hostname while 'payload.event.origin' === https://hostname resulting in mismatch
     * - Chrome extension interferes 'payload.event.origin'. Multiple people with "chrome-extension://efaidnbmnnnibpcajpcglclefindmkaj" aka Adobe Acrobat
     */
    postMessageError(newVal, oldVal) {
      if (!oldVal && newVal) {
        log.debug('[watch.postMessageError] %o', { newVal, oldVal });
        const unraidUrl = newVal['data.server.site'] ? new URL(newVal['data.server.site']) : null;
        const browserUrl = newVal['payload.event.origin'] ? new URL(newVal['payload.event.origin']) : null;
        // Possible Reverse Proxy detection – matching hostname but mismatching protocols
        if (unraidUrl && browserUrl && unraidUrl.hostname === browserUrl.hostname && unraidUrl.protocol !== browserUrl.protocol) {
          const extraOriginsMsg = 'In your webGUI go to Settings > Management Access > "Extra Origins" field – Add your Reverse Proxy URL > Apply. Then close this window and try again. <em>Note: reverse proxy is not officially supported by Unraid.</em>';
          const noRvrsPrxyMsg = 'Since reverse proxy is not officially supported by Unraid please try without it.';
          this.SET_APP_ERROR_MESSAGE_SUBTEXT(`We have detected a mismatch in URLs – this could be due to a reverse proxy. ${(this.apiVersion && semverGte(this.apiVersion, '2.50.0')) ? extraOriginsMsg : noRvrsPrxyMsg }`);
        }
        this.SET_APP_ERROR_MESSAGE('PostMessage from webgui failed');
        this.SET_LOADING_STATE(false);
        clearInterval(this.checkForSession); // stop the interval below
        this.$router.push({ name: 'Troubleshoot' });
      }
    },
  },
  beforeCreate() {
    const msg = loadLocaleMessages();
    this.$i18n.mergeLocaleMessage(this.$i18n.locale, msg[this.$i18n.locale]);
    log.debug(`[beforeCreate.$i18n] addt'l default locale messages merged into existing ${this.$i18n.locale} messages`, this.$i18n.messages);
  },
  created() {
    log.debug('[CAN_POST_MESSAGE]', CAN_POST_MESSAGE);
    if (!CAN_POST_MESSAGE) { // hold the presses!! we're not in a pop-up so don't even try.
      this.SET_LOADING_STATE(false);
      // log.debug('[CAN_POST_MESSAGE]', 'redirect to home');
      // this.$router.push({ name: 'Registration' });
      return;
    }
    // ask UPC for the server state
    log.debug('[created] send PREFLIGHT_REQUEST %o', WEBGUI_PARENT);
    WEBGUI_PARENT.postMessage(JSON.stringify({ event: 'PREFLIGHT_REQUEST' }), '*'); // targetOrigin is catchAll because we don't have `this.site` yet
    // here to listen for postMessages from creator of pop-up window
    window.addEventListener('message', (event) => {
      this.receivePostMessage({ event });
    }, false);
  },
  beforeMount() {
    // hold the presses!! we're not in a pop-up so don't even try.
    if (!CAN_POST_MESSAGE) {
      // const msg = 'PostMessage not allowed. Registration must be started from your Unraid webgui.';
      // this.SET_APP_ERROR_MESSAGE(msg);
      // return log.error(`${msg} %o`, { CAN_POST_MESSAGE });
      this.SET_LOADING_STATE(false);
      return log.log('Skip postmessages');
    }

    const INTERVAL = 100; // milliseconds
    const ATTEMPTS_ALLOWED = 100; // ${INTERVAL}ms * 100 = 10000ms or 10seconds
    let count = 0;

    this.checkForSession = setInterval(() => { // we need to keep checking for the presense of the SESSION
      if (this.guid) { // the value of guid is used to know we received the postMessage
        clearInterval(this.checkForSession); // stop the interval
        this.SET_LOADING_STATE(false);
        // this.validateGuid().then(() => { // after validation if not VALID & not able to sign in to fix we redirect to home if necessary to show error message
        //   if (!this.guidValid && !this.stateData.error?.signInToFix && this.$router.currentRoute.name !== 'Registration') {
        //     this.$router.push({ name: 'Registration' });
        //   }
        // });
      }
      if (count >= ATTEMPTS_ALLOWED) { // TIMED OUT took too long to get data from webgui postMessage
        clearInterval(this.checkForSession); // stop the interval
        this.SET_APP_ERROR_MESSAGE('Timed out, pop-up failed to receive data from webgui');
        this.SET_APP_ERROR_MESSAGE_SUBTEXT('Your browser may be preventing communication between the main window and the sign in pop-up. This may be due to a browser extension or extreme privacy settings in the browser itself. Try adding an exception for *.myunraid.net, *.unraid.net, and *.lime-technology.com (and your webgui URL) in any ad blocker or privacy configuration you have. Or try signing in using a different browser with less restrictive settings.');
        this.SET_LOADING_STATE(false);
        this.$router.push({ name: 'Troubleshoot' });
      }
      count += 1;
    }, INTERVAL);
    return true;
  },
};
</script>

<style lang="postcss">
@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  color-scheme: light dark; /* both supported */
}

/*! purgecss start ignore */
::-moz-selection { @apply text-white bg-orange }
::selection { @apply text-white bg-orange }

.fade-enter-active, .fade-leave-active {
  transition: opacity .15s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion) {
  * { transition-duration: 0 !important }
}
/*! purgecss end ignore */
</style>
